import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Badge, cn, type BadgeTone } from '@hrms/ui';
import type {
  AnnouncementAudience,
  AnnouncementCategory,
  AnnouncementReaction,
  AnnouncementStatus,
  MyAnnouncement,
} from '@hrms/shared';

/**
 * Pieces shared by the employee feed, the Home card and the admin pages (ADR 0029). The file helpers
 * and error text are the company-policy pages' own. The Markdown body (`PolicyBodyText`) is NOT
 * re-exported here: the Home card imports this file, and a re-export would pull the Markdown renderer
 * into every person's first load — the pages that render a body import it directly.
 */
export { policyErrorMessage as announcementErrorMessage } from '../company-policies/policyShared.js';
export { fileToBase64, openBase64File } from '../../lib/base64-file.js';
export { downloadCsv } from '../../lib/csv-download.js';

/** Every announcement query sits under this key, so one invalidation refreshes feed, card and admin. */
export const ANNOUNCEMENT_QUERY_KEY = ['announcements'] as const;

export function useInvalidateAnnouncements() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: ANNOUNCEMENT_QUERY_KEY });
}

export const CATEGORY_LABELS: Record<AnnouncementCategory, string> = {
  policy_update: 'Policy Update',
  culture_events: 'Culture & Events',
  executive: 'Executive',
  benefits: 'Benefits',
};

export const REACTIONS: readonly { key: AnnouncementReaction; emoji: string; label: string }[] = [
  { key: 'like', emoji: '👍', label: 'Like' },
  { key: 'celebrate', emoji: '🎉', label: 'Celebrate' },
  { key: 'love', emoji: '❤️', label: 'Love' },
  { key: 'insightful', emoji: '💡', label: 'Insightful' },
  { key: 'support', emoji: '🙌', label: 'Support' },
];

const STATUS_TONE: Record<AnnouncementStatus, BadgeTone> = {
  draft: 'neutral',
  scheduled: 'info',
  live: 'success',
  expired: 'warning',
  archived: 'neutral',
};

/** Sentence case like every other module's status pill — never the raw enum. */
const STATUS_LABEL: Record<AnnouncementStatus, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  live: 'Live',
  expired: 'Expired',
  archived: 'Archived',
};

export function AnnouncementStatusBadge({ status }: { status: AnnouncementStatus }) {
  return <Badge tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</Badge>;
}

/** "Everyone", or what the audience is narrowed by: "2 locations · 1 department". */
export function audienceSummary(audience: AnnouncementAudience): string {
  const parts = (
    [
      [audience.legalEntityIds.length, 'legal entity', 'legal entities'],
      [audience.locationIds.length, 'location', 'locations'],
      [audience.departmentIds.length, 'department', 'departments'],
      [audience.employeeGroupIds.length, 'employee group', 'employee groups'],
    ] as const
  )
    .filter(([n]) => n > 0)
    .map(([n, one, many]) => `${n} ${n === 1 ? one : many}`);
  return parts.length ? parts.join(' · ') : 'Everyone';
}

/** The small dot the feed and the Home card show on an announcement the caller has not opened. */
export function UnreadMarker({ className }: { className?: string }) {
  return (
    <span
      role="img"
      aria-label="Unread"
      className={cn('inline-block size-2 shrink-0 rounded-full bg-accent', className)}
    />
  );
}

/**
 * Reaction chips: the counts, plus one button per reaction when the caller may react. Clicking
 * the caller's own reaction clears it (one reaction per person, ADR 0029 §4).
 */
export function ReactionBar({
  announcement,
  onReact,
  busy,
}: {
  announcement: Pick<MyAnnouncement, 'allowReactions' | 'reactions' | 'myReaction'>;
  onReact?: (reaction: AnnouncementReaction | null) => void;
  busy?: boolean;
}) {
  if (!announcement.allowReactions) return null;
  const counts = new Map(announcement.reactions.map((r) => [r.reaction, r.count]));
  const shown = onReact ? REACTIONS : REACTIONS.filter((r) => counts.has(r.key));
  if (shown.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {shown.map((r) => {
        const mine = announcement.myReaction === r.key;
        const count = counts.get(r.key) ?? 0;
        const content = (
          <>
            <span aria-hidden="true">{r.emoji}</span>
            {count > 0 && <span className="tabular-nums">{count}</span>}
          </>
        );
        const chip = cn(
          'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs',
          mine ? 'border-accent bg-accent-subtle text-accent' : 'border-border text-fg-muted',
        );
        return onReact ? (
          <button
            key={r.key}
            type="button"
            className={cn(chip, 'hover:bg-bg-subtle disabled:opacity-60')}
            aria-pressed={mine}
            aria-label={r.label}
            disabled={busy}
            onClick={() => onReact(mine ? null : r.key)}
          >
            {content}
          </button>
        ) : (
          <span key={r.key} className={chip} title={r.label}>
            {content}
          </span>
        );
      })}
    </div>
  );
}

/** A base64 image from a download route, as an object URL for an <img>; revoked on unmount. */
export function useBase64ImageUrl(
  file: { mimeType: string; contentBase64: string } | null | undefined,
): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setUrl(null);
      return;
    }
    const binary = atob(file.contentBase64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    const next = URL.createObjectURL(new Blob([bytes], { type: file.mimeType }));
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);
  return url;
}
